import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { TransactionsService } from './transactionsApi';
import { IPaginationPayload } from '../../../../interfaces/shared/IPaginationPayload';
import { PaginatedTransactions } from '../../../../interfaces/transactions/pagiantedStore.type';
import { ITransactionsListState } from '../../../../interfaces/transactions/ITransactionsListState';
import { AxiosError } from 'axios';

const initialState: ITransactionsListState = {
    transactionsAreLoading: false,
    monthlyAreLoading: false,
    transactionIsLoading: false,
    runsAreLoading: false,
    requestStatus: 'default',
    message: '',
};

export const tryToFetchAllTransactions = createAsyncThunk<any, any>(
    'transactions/tryToFetchAllTransactions',
    async (data: any) => {
        const result = await TransactionsService.tryToFetchAllTransactions(data);
        return result?.data;
    }
);

export const tryToFetchTransactionFilters = createAsyncThunk<any, any>(
    'transactions/tryToFetchTransactionFilters',
    async (data, { rejectWithValue }) => {
        try {
            const result = await TransactionsService.tryToFetchTransactionFilters(data);
            return result?.data?.data;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message);
        }
    }
);

export const tryToExportTransactionReport = createAsyncThunk<any, any>(
    'transactions/tryToExportTransactionReport',
    async (data: any) => {
        const result = await TransactionsService.tryToExportTransactionReport(data);
        return result?.data;
    }
);

export const tryToFetchMonthlyBank = createAsyncThunk<any, any>(
    'transactions/tryToFetchMonthlyBank',
    async (data: any) => {
        const result = await TransactionsService.tryToFetchMonthlyBank(data);
        return result?.data?.data;
    }
);

export const tryToDownloadMonthlyBank = createAsyncThunk<any, any>(
    'transactions/tryToDownloadMonthlyBank',
    async (data: any) => {
        const result = await TransactionsService.tryToFetchMonthlyBank(data);
        return result?.data?.data;
    }
);

export const tryToFetchTransactions = createAsyncThunk<PaginatedTransactions, IPaginationPayload>(
    'transactions/tryToFetchTransactions',
    async ({ pageNumber, pageSize, filters, data, status }) => {
        const result = await TransactionsService.tryToFetchTransactions(
            pageNumber,
            pageSize,
            data,
            filters,
			status
        );
        return result?.data;
    },
);


export const tryToFetchTransactionsUnreconciled = createAsyncThunk<PaginatedTransactions, IPaginationPayload>(
    'transactions/tryToFetchTransactionsUnreconciled',
    async ({ pageNumber, pageSize, filters, data }) => {
        const result = await TransactionsService.tryToFetchTransactions(
            pageNumber,
            pageSize,
            data,
            filters,
        );
        return result?.data;
    },
);

export const tryToFetchUnreconciledTransactions = createAsyncThunk<PaginatedTransactions, IPaginationPayload>(
    'transactions/tryToFetchUnreconciledTransactions',
    async ({ pageNumber, pageSize, filters, data }) => {
        const result = await TransactionsService.tryToFetchUnreconciledTransactions(
            pageNumber,
            pageSize,
            data,
            filters,
        );
        return result?.data;
    },
);

export const tryToFetchTransactionsRuns = createAsyncThunk<any, any>(
    'transactions/tryToFetchTransactionsRuns',
    async ({ pageNumber, pageSize, filters, status }: any) => {
        const result = await TransactionsService.tryToFetchTransactionsRuns(
            pageNumber,
            pageSize,
            filters,
			status
        );
        return result?.data;
    },
);

export const tryToReviewTransaction = createAsyncThunk<any, any>(
    'transactions/tryToReviewTransaction',
    async (data, { rejectWithValue }) => {
        try {
            const result =
                await TransactionsService.tryToReviewTransaction(data);
            return result.data?.data;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message);
        }
    },
);

export const tryToUploadManualTransaction = createAsyncThunk<any, any>(
    'transactions/tryToUploadManualTransaction',
    async (data, { rejectWithValue }) => {
        try {
            const result =
                await TransactionsService.tryToUploadManualTransaction(data);
            return result.data;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message);
        }
    },
);

export const transactionsSlice = createSlice({
    name: 'transactions',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            // tryToFetchAllTransactions
            .addCase(tryToFetchAllTransactions.pending, (state) => {
                state.transactionIsLoading = true;
            })
            .addCase(tryToFetchAllTransactions.rejected, (state) => {
                state.transactionIsLoading = false;
            })
            .addCase(tryToFetchAllTransactions.fulfilled, (state, action) => {
                state.transactionIsLoading = false;
                state.allTransactions = action.payload;
            })

            // tryToFetchTransactions
            .addCase(tryToFetchTransactions.pending, (state) => {
                state.transactionsAreLoading = true;
            })
            .addCase(tryToFetchTransactions.rejected, (state) => {
                state.transactionsAreLoading = false;
            })
            .addCase(tryToFetchTransactions.fulfilled, (state, action) => {
                state.transactionsAreLoading = false;
                const incoming = action.payload;
                const incomingPageNumber = incoming?.data?.page?.number;

                if (state.transactions && incomingPageNumber && incomingPageNumber > 1) {
                    state.transactions = {
                        ...incoming,
                        data: {
                            ...incoming.data,
                            elements: [
                                ...(state.transactions?.data?.elements || []),
                                ...(incoming.data?.elements || []),
                            ],
                        },
                    };
                } else {
                    state.transactions = incoming;
                }
            })

            // tryToFetchMonthlyBank
            .addCase(tryToFetchMonthlyBank.pending, (state) => {
                state.monthlyAreLoading = true;
            })
            .addCase(tryToFetchMonthlyBank.rejected, (state) => {
                state.monthlyAreLoading = false;
            })
            .addCase(tryToFetchMonthlyBank.fulfilled, (state, action) => {
                state.monthlyAreLoading = false;
                state.bankMonthly = action.payload;
            })

            // tryToFetchTransactionsUnreconciled
            .addCase(tryToFetchTransactionsUnreconciled.pending, (state) => {
                state.transactionIsLoading = true;
            })
            .addCase(tryToFetchTransactionsUnreconciled.rejected, (state) => {
                state.transactionIsLoading = false;
            })
            .addCase(tryToFetchTransactionsUnreconciled.fulfilled, (state, action) => {
                state.transactionIsLoading = false;
                const incoming = action.payload;
                const incomingPageNumber = incoming?.data?.page?.number;

                if (state.filteredTransactions && incomingPageNumber && incomingPageNumber > 1) {
                    state.filteredTransactions = {
                        ...incoming,
                        data: {
                            ...incoming.data,
                            elements: [
                                ...(state.filteredTransactions?.data?.elements || []),
                                ...(incoming.data?.elements || []),
                            ],
                        },
                    };
                } else {
                    state.filteredTransactions = incoming;
                }
            })

            // tryToFetchUnreconciledTransactions
            .addCase(tryToFetchUnreconciledTransactions.pending, (state) => {
                state.transactionIsLoading = true;
            })
            .addCase(tryToFetchUnreconciledTransactions.rejected, (state) => {
                state.transactionIsLoading = false;
            })
            .addCase(tryToFetchUnreconciledTransactions.fulfilled, (state, action) => {
                state.transactionIsLoading = false;
                state.unreconciled = action.payload;
            })

            // tryToFetchTransactionsRuns
            .addCase(tryToFetchTransactionsRuns.pending, (state) => {
                state.runsAreLoading = true;
            })
            .addCase(tryToFetchTransactionsRuns.rejected, (state) => {
                state.runsAreLoading = false;
            })
            .addCase(tryToFetchTransactionsRuns.fulfilled, (state, action) => {
                state.runsAreLoading = false;
                state.runs = action.payload;
            })

			// tryToUploadManualTransaction
            .addCase(tryToUploadManualTransaction.pending, (state) => {
                state.transactionIsLoading = true;
            })
            .addCase(tryToUploadManualTransaction.rejected, (state) => {
                state.transactionIsLoading = false;
            })
            .addCase(tryToUploadManualTransaction.fulfilled, (state, action) => {
                state.transactionIsLoading = false;
                state.transactions = {
					...state.transactions,
					elements: [action.payload, ...state.transactions.elements || []],
				  }
            })
    },
});

export default transactionsSlice.reducer;